import { createSelector } from 'reselect';

import { UNREAD_STATUS } from '../shared/constants';

import { StateType } from './index';
import { alphasTeamOwnerSelector } from './AlphasReducer/AlphasSelector';
import { getUserIdSelector, getUserIsFetchingSelector } from './AuthenticationReducer/AuthenticationSelector';
import { profileAchievementsIsFetchingSelector } from './ProfileReducer/AchievementsReducer/AchievementsSelector';
import { userProfileDataIdSelector } from './ProfileReducer/UserReducer/UserProfileSelector';
import { activeChildTabSelector, activeTabIndexSelector } from './EditorReducer/EditorSelector';
import { AlphasStoreElement } from './AlphasReducer/AlphasReducer.types';

const dataSelector = (state: StateType) => state.getIn(['simulation', 'data']);

/*
  This selector uses the variable path to access different reducers where the information structure is the same.
  This first reducer gets all selection code settings
*/
export const getSelectionCodeSettingsSelector = (state: StateType, path: string[]) => {
  const completePath = path.concat(['selectionCodeSettings']);

  return state.getIn(completePath);
};

// This selector uses the getSelectionCodeSettingsSelector to get the selection code
export const codeFromselectedAlphasSettingsSelector = createSelector(
  getSelectionCodeSettingsSelector,
  // @ts-ignore
  data => data && data.get('selection')
);

// This selector uses the getSelectionCodeSettingsSelector to get the instrument Type
export const instrumentTypeFromselectedAlphasSettingsSelector = createSelector(
  getSelectionCodeSettingsSelector,
  // @ts-ignore
  data => data && data.get('instrumentType')
);

// This selector uses the getSelectionCodeSettingsSelector to get the region
export const regionFromselectedAlphasSettingsSelector = createSelector(
  getSelectionCodeSettingsSelector,
  // @ts-ignore
  data => data && data.get('region')
);

// This selector uses the getSelectionCodeSettingsSelector to get the delay
export const delayFromselectedAlphasSettingsSelector = createSelector(
  getSelectionCodeSettingsSelector,
  // @ts-ignore
  data => data && data.get('delay')
);

// This selector uses the getSelectionCodeSettingsSelector to get the selection limit
export const selectionLimitFromSelectedAlphasSettingsSelector = createSelector(
  getSelectionCodeSettingsSelector,
  // @ts-ignore
  data => data && data.get('selectionLimit')
);

// This selector uses the getSelectionCodeSettingsSelector to get the selection Handling
export const selectionHandlingFromSelectedAlphasSettingsSelector = createSelector(
  getSelectionCodeSettingsSelector,
  // @ts-ignore
  data => data && data.get('selectionHandling')
);

// This selector uses the data reducer to get all tabs which the status is unread
export const selectionHasSimulationsUnread = createSelector(
  dataSelector,
  // @ts-ignore
  tabsData => (tabsData ? tabsData.filter((tabData: any) => tabData.get('status') === UNREAD_STATUS).size > 0 : false)
);

export const tabsStatusSelector = createSelector(
  dataSelector,
  tabs =>
    tabs &&
    // @ts-ignore
    tabs.map((parentTab: any) => ({
      parentTab: {
        currentStatus: parentTab.get('status'),
        statusAfterSimulation: parentTab.get('statusAfterSimulation'),
      },
      childTabs: parentTab.get('children')
        ? parentTab.get('children').map((childTab: any) => ({
            currentStatus: childTab.get('status'),
            statusAfterSimulation: childTab.get('statusAfterSimulation'),
          }))
        : [],
    }))
);

export interface ActiveTabStatusSelection {
  tabIndex: number | undefined;
  childTabIndex: number | undefined;
  tabStatus: string | undefined;
}

export const activeTabStatusSelector = createSelector(
  [dataSelector, activeTabIndexSelector, activeChildTabSelector],
  (data, tabIndex, childTabIndex): ActiveTabStatusSelection => {
    const isChildTab = childTabIndex !== undefined;
    const tabStatus = isChildTab
      ? data && (data as any).getIn([tabIndex, 'children', childTabIndex, 'status'])
      : data && (data as any).getIn([tabIndex, 'status']);
    return { tabIndex: tabIndex as number | undefined, childTabIndex, tabStatus };
  }
);

export const checkIfProfileIdIsEqualToUserId = createSelector(
  userProfileDataIdSelector,
  getUserIdSelector,
  (userProfileId, currentUserId) => userProfileId === currentUserId
);

export const isFetchingGeneralProfile = createSelector(
  getUserIsFetchingSelector,
  profileAchievementsIsFetchingSelector,
  (isFetching, isFetchingAchievements) => isFetching || isFetchingAchievements
);

// @ts-ignore
export const isAlphaCreatedByTheCurrentUserSelector = createSelector(
  getUserIdSelector,
  alphasTeamOwnerSelector,
  (userId: string, alphaUserId: string) => (alphaUserId && alphaUserId.length > 0 ? alphaUserId === userId : false)
);

export function getStartDates(state: AlphasStoreElement) {
  const osStartDate = state.getIn(['alphaDetail', 'os', 'startDate']);
  const testStartDate = state.getIn(['alphaDetail', 'test', 'startDate']);
  const validationStartDate = state.getIn(['alphaDetail', 'validation', 'startDate']);
  const trainStartDate = state.getIn(['alphaDetail', 'validation', 'startDate']);
  return { osStartDate, testStartDate, trainStartDate, validationStartDate };
}
